import * as React from 'react';
import {
  MasonryEngineNode,
  MasonryEngineNodeAction,
  MasonryEngineNodeRendererProps,
  useMasonryEngineActionEmitter,
} from '@leagueplatform/masonry-engine';
import {
  HeadingText,
  HtmlToReact,
  Image,
  StackLayout,
  Box,
  GDSHeadingLevel,
  ParagraphText,
} from '@leagueplatform/genesis-core';
import { MasonryButton } from '@leagueplatform/masonry-renderers';

export const NODE_TYPE_ORDER_NEW_CARD_SUCCESS_SCREEN =
  'orderNewCardSuccessScreen';

type OrderNewCardSuccessScreenProps = {
  image?: {
    url: string;
    description?: string;
    width?: string;
    height?: string;
  };
  heading: string;
  headingLevel?: GDSHeadingLevel;
  body: string;
  closeButtonLabel: string;
};

export type OrderNewCardSuccessScreenNodeActions = {
  onLoad?: MasonryEngineNodeAction;
  onCloseClick?: MasonryEngineNodeAction;
};

type OrderNewCardSuccessScreenNode = MasonryEngineNode<
  typeof NODE_TYPE_ORDER_NEW_CARD_SUCCESS_SCREEN,
  OrderNewCardSuccessScreenProps,
  OrderNewCardSuccessScreenNodeActions,
  {}
>;

export type OrderNewCardSuccessScreenRendererProps =
  MasonryEngineNodeRendererProps<OrderNewCardSuccessScreenNode>;

export function OrderNewCardSuccessScreenRenderer({
  image,
  heading,
  headingLevel = '3',
  body,
  closeButtonLabel,
  onCloseClick,
}: OrderNewCardSuccessScreenRendererProps) {
  const invokeAction = useMasonryEngineActionEmitter();

  const handleCloseClick = React.useCallback(
    (event: React.SyntheticEvent) => {
      if (onCloseClick) {
        invokeAction({ event, ...onCloseClick });
      }
    },
    [invokeAction, onCloseClick],
  );

  const hasCloseButton = closeButtonLabel !== '' && onCloseClick !== undefined;

  return (
    <Box
      css={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box>
        <StackLayout
          spacing="$oneAndHalf"
          css={{
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          {image?.url ? (
            <Image
              src={image.url}
              alt={image.description ?? ''}
              width={image.width}
              height={image.height}
            />
          ) : null}

          <StackLayout
            spacing="$half"
            css={{ justifyContent: 'center', alignItems: 'center' }}
          >
            <HeadingText level={headingLevel} size="md">
              {heading}
            </HeadingText>
            <HtmlToReact
              htmlString={body}
              options={{
                p: { component: ParagraphText, props: { emphasis: 'subtle' } },
              }}
            />
          </StackLayout>

          {hasCloseButton && (
            <StackLayout
              spacing="$threeQuarters"
              css={{ justifyContent: 'center', alignItems: 'center' }}
            >
              <MasonryButton
                priority="primary"
                size="medium"
                onClick={handleCloseClick}
                action={onCloseClick}
              >
                {closeButtonLabel}
              </MasonryButton>
            </StackLayout>
          )}
        </StackLayout>
      </Box>
    </Box>
  );
}
